import { ColumnDef } from "@tanstack/react-table";
import { parseISO } from "date-fns";
import {
  ArrowRightLeft,
  ChartSpline,
  CircleCheck,
  Download,
  ExternalLink,
  Eye,
  Loader,
  MoreHorizontal,
  Pencil,
  Tags,
  Trash,
} from "lucide-react";
import { useMemo } from "react";
import { Link } from "react-router";

import { ServerDataTable } from "@/components/server-data-table";
import { CreateSKUButton } from "@/components/skus/create-sku-button";
import { SkuImage } from "@/components/skus/sku-image";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useExportSKUsMutation } from "@/data/mutations";
import { useGetSKUsQuery } from "@/data/queries";
import { SKU } from "@/data/types";
import { useDialogState } from "@/hooks/use-dialog-state";
import { generateAddressLink, NetworkNameByChainId } from "@/lib/chains";
import { canEditSKU } from "@/lib/permissions";
import useStore from "@/state";

const columns: ColumnDef<SKU>[] = [
  {
    id: "id",
    accessorKey: "id",
    header: "ID",
    cell: ({ row }) => {
      return (
        <Button variant="secondary" size="sm" asChild className="w-16">
          <Link to={`/skus/${row.original.id}`}>
            <Pencil /> {row.original.id}
          </Link>
        </Button>
      );
    },
  },
  {
    id: "name",
    accessorKey: "name",
    header: "Name",
    cell: ({ row }) => {
      return (
        <div className="flex items-center gap-2 w-125">
          <SkuImage
            src={row.original.image_url}
            alt={row.original.name}
            srcSetWidths={[200, 400]}
            className="max-w-10 max-h-10 min-w-10 min-h-10"
          />
          <div className="min-w-0">
            <p className="font-medium truncate">{row.original.name}</p>
            <p className="text-sm text-muted-foreground truncate">
              {row.original.description}
            </p>
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "notes",
    header: "SKU Notes",
    cell: ({ row }) => {
      return (
        <div className="w-61">
          <p className="truncate">{row.original.notes}</p>
        </div>
      );
    },
  },
  {
    header: "Looker",
    accessorKey: "analytics_url",
    cell: ({ row }) => {
      if (!row.original.analytics_url) {
        return (
          <Button variant="outline" className="text-default w-25" disabled>
            <ExternalLink size={16} />
          </Button>
        );
      }
      return (
        <Button variant="outline" className="text-default w-25" asChild>
          <Link to={row.original.analytics_url || ""} target="_blank">
            <ExternalLink size={16} />
          </Link>
        </Button>
      );
    },
  },
  {
    header: "Analytics",
    accessorKey: "analytics",
    cell: ({ row }) => {
      return (
        <Button variant="outline" className="text-default w-12" asChild>
          <Link to={`/skus/${row.original.id}/analytics`}>
            <ChartSpline size={16} />
          </Link>
        </Button>
      );
    },
  },
  {
    id: "templates.tag_view_verifications_count",
    accessorKey: "tag_view_verifications_count",
    header: "Page Views",
    cell: ({ row }) => {
      return (
        <Button variant="outline" size="sm" className="w-22" asChild>
          <Link
            to={`/verifications?templateId=${row.original.id}&verifyType=user_view`}
          >
            <Eye /> {row.original.tag_view_verifications_count}
          </Link>
        </Button>
      );
    },
  },
  {
    id: "templates.tags_count",
    accessorKey: "tags_count",
    header: "Tags",
    cell: ({ row }) => {
      return (
        <Button variant="outline" size="sm" className="w-22" asChild>
          <Link to={`/tags?templateId=${row.original.id}`}>
            <Tags /> {row.original.tags_count}
          </Link>
        </Button>
      );
    },
  },
  {
    id: "created_at",
    accessorKey: "created_at",
    header: "Created at",
    cell: ({ row }) => {
      return (
        <div className="w-21">
          {parseISO(row.original.created_at).toLocaleDateString()}
          <br />
          {parseISO(row.original.created_at).toLocaleTimeString()}
        </div>
      );
    },
  },
  {
    header: "Quality Check",
    cell: ({ row }) => {
      if (row.original.tags_count === row.original.verified_tags_count) {
        return (
          <Badge variant="green" className="w-20">
            Passed
          </Badge>
        );
      }

      return (
        <Badge variant="orange" className="w-20">
          {row.original.verified_tags_count}/{row.original.tags_count}
        </Badge>
      );
    },
  },
  {
    id: "tag_verifications_count",
    accessorKey: "tag_verifications_count",
    header: "Total Verifications",
    cell: ({ row }) => {
      return (
        <Button variant="outline" size="sm" asChild className="w-22">
          <Link to={`/verifications?templateId=${row.original.id}`}>
            <CircleCheck /> {row.original.tag_verifications_count}
          </Link>
        </Button>
      );
    },
  },
  {
    id: "external_tag_verifications_count",
    accessorKey: "external_tag_verifications_count",
    header: "API Tag Verifications",
    cell: ({ row }) => {
      return (
        <div className="w-20 text-center">
          {row.original.external_tag_verifications_count}
        </div>
      );
    },
  },
  {
    header: "Blockchain",
    accessorKey: "nft_chain_id",
    cell: ({ row }) => {
      if (!row.original.nft_chain_id) {
        return <div className="w-0"></div>;
      } else {
        return (
          <Link
            to={generateAddressLink(
              row.original.nft_chain_id,
              row.original.nft_contract_address || "",
            )}
            target="_blank"
          >
            <Badge variant="secondary" className="w-30">
              {
                NetworkNameByChainId[
                  row.original.nft_chain_id as keyof typeof NetworkNameByChainId
                ]
              }
            </Badge>
          </Link>
        );
      }
    },
  },
  {
    id: "actions",
    header: "Actions",
    accessorKey: "actions",
    cell: ({ row }) => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const { openDialog } = useDialogState();

      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="w-8">
              <MoreHorizontal />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-36">
            <Link to={`/skus/${row.original.id}`}>
              <DropdownMenuItem>
                <Pencil /> Edit SKU
              </DropdownMenuItem>
            </Link>
            <DropdownMenuItem
              onClick={() =>
                openDialog({
                  name: "transfer-sku",
                  params: { skuId: row.original.id },
                })
              }
            >
              <ArrowRightLeft /> Transfer SKU
            </DropdownMenuItem>
            <DropdownMenuItem
              variant="destructive"
              onClick={() =>
                openDialog({
                  name: "delete-sku",
                  params: { skuId: row.original.id },
                })
              }
            >
              <Trash /> Delete SKU
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
];

const SKUS_DEFAULT_SORT = { column: "id", direction: "desc" } as const;

export const Skus = () => {
  const { session } = useStore();
  const exportMutation = useExportSKUsMutation();
  const { data, isError, error, isFetching } = useGetSKUsQuery({
    defaultSort: SKUS_DEFAULT_SORT,
  });

  const handleExport = async () => {
    exportMutation.mutateAsync();
  };

  const skuEditAllowed = useMemo(
    () => canEditSKU(session?.permissions),
    [session],
  );

  if (isError) {
    return <div>Error: {error.message}</div>;
  }

  return (
    <div>
      <div className="flex justify-between border-b pb-2">
        <h2 className="scroll-m-20 text-3xl font-medium tracking-tight first:mt-0">
          SKUs
        </h2>
        <CreateSKUButton />
      </div>
      <ServerDataTable
        isLoading={isFetching}
        columns={columns}
        data={data?.templates}
        paginationInfo={data?.pagy}
        filterPlaceholder="Filter by ID, name, description, or notes"
        defaultColumnVisibility={{
          created_at: false,
          nft_chain_id: false,
          tag_verifications_count: false,
          external_tag_verifications_count: false,
          actions: skuEditAllowed,
          analytics_url: false,
        }}
        extraButtons={
          <Button
            variant="secondary"
            disabled={exportMutation.isPending}
            onClick={handleExport}
          >
            {exportMutation.isPending ? (
              <Loader className="animate-spin" />
            ) : (
              <Download />
            )}
            Download CSV
          </Button>
        }
        sortableColumns={[
          "id",
          "name",
          "templates.tag_view_verifications_count",
          "templates.tags_count",
          "created_at",
        ]}
        defaultSort={SKUS_DEFAULT_SORT}
      />
    </div>
  );
};
